import React from 'react';
import { DropZone } from '@measured/puck';
import { resolveBackgroundColor, backgroundColorOptions } from './theme/colors';

const StackComponent = ({
  gap = 'lg',
  align = 'stretch',
  backgroundColor = 'transparent',
  padding = 'xl',
  maxWidth = '6xl',
  border = 'none',
  borderColor = 'gray-200',
  borderRadius = 'lg'
}) => {
  const gapMapping = {
    none: '0px',
    xs: '0.5rem',
    sm: '0.75rem',
    md: '1rem',
    lg: '1.5rem',
    xl: '2rem'
  };

  const containerBackground = resolveBackgroundColor(backgroundColor, 'transparent');

  const getPaddingClass = () => {
    const mapping = {
      none: 'py-0 px-0',
      sm: 'py-8 px-4',
      md: 'py-12 px-4 md:px-6',
      lg: 'py-16 px-6 md:px-8',
      xl: 'py-20 px-6 md:px-10'
    };
    return mapping[padding] || 'py-20 px-6 md:px-10';
  };

  const getMaxWidthClass = () => {
    const mapping = {
      '4xl': 'max-w-4xl',
      '5xl': 'max-w-5xl',
      '6xl': 'max-w-6xl',
      '7xl': 'max-w-7xl',
      full: 'max-w-full'
    };
    return mapping[maxWidth] || 'max-w-6xl';
  };

  const getBorderClasses = () => {
    if (border === 'none') return '';
    const borderColorMapping = {
      'gray-200': 'border border-gray-200 dark:border-slate-700',
      'gray-300': 'border border-gray-300 dark:border-slate-600',
      'blue-200': 'border border-blue-200 dark:border-blue-800',
      'slate-700': 'border border-slate-700'
    };
    return borderColorMapping[borderColor] || 'border border-gray-200';
  };

  const getRadiusClass = () => {
    const mapping = {
      none: 'rounded-none',
      sm: 'rounded-md',
      lg: 'rounded-xl',
      xl: 'rounded-2xl'
    };
    return mapping[borderRadius] || 'rounded-xl';
  };

  const alignmentStyles = {
    stretch: 'stretch',
    start: 'flex-start',
    center: 'center',
    end: 'flex-end'
  };

  const resolvedGap = gapMapping[gap] || gapMapping.lg;
  const resolvedAlign = alignmentStyles[align] || alignmentStyles.stretch;
  const containerPadding = {
    none: '0px',
    xs: '0.75rem',
    sm: '1rem',
    md: '1.25rem',
    lg: '1.5rem',
    xl: '2rem'
  }[gap] || '1.5rem';

  return (
    <section className={`w-full ${containerBackground.className}`} style={containerBackground.style}>
      <div className={`${getMaxWidthClass()} mx-auto ${getPaddingClass()}`}>
        <div className={`${getBorderClasses()} ${getRadiusClass()} h-full bg-white/90 dark:bg-slate-900/40`}> 
          <DropZone
            zone="stack-content"
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: resolvedGap,
              alignItems: resolvedAlign,
              width: '100%',
              height: '100%',
              padding: border === 'none' && backgroundColor === 'transparent' ? '0px' : containerPadding
            }}
          />
        </div>
      </div>
    </section>
  );
};

export const Stack = {
  fields: {
    gap: {
      type: 'select',
      label: 'Abstand zwischen Elementen',
      options: [
        { label: 'Kein', value: 'none' },
        { label: 'Sehr klein', value: 'xs' },
        { label: 'Klein', value: 'sm' },
        { label: 'Mittel', value: 'md' },
        { label: 'Groß', value: 'lg' },
        { label: 'Sehr groß', value: 'xl' }
      ]
    },
    align: {
      type: 'select',
      label: 'Horizontale Ausrichtung',
      options: [
        { label: 'Strecken', value: 'stretch' },
        { label: 'Links', value: 'start' },
        { label: 'Zentriert', value: 'center' },
        { label: 'Rechts', value: 'end' }
      ]
    },
    backgroundColor: {
      type: 'select',
      label: 'Hintergrund',
      options: backgroundColorOptions()
    },
    padding: {
      type: 'select',
      label: 'Außenabstand',
      options: [
        { label: 'Kein', value: 'none' },
        { label: 'Klein', value: 'sm' },
        { label: 'Mittel', value: 'md' },
        { label: 'Groß', value: 'lg' },
        { label: 'Sehr groß', value: 'xl' }
      ]
    },
    maxWidth: {
      type: 'select',
      label: 'Maximale Breite',
      options: [
        { label: '4XL', value: '4xl' },
        { label: '5XL', value: '5xl' },
        { label: '6XL', value: '6xl' },
        { label: '7XL', value: '7xl' },
        { label: 'Vollbreite', value: 'full' }
      ]
    },
    border: {
      type: 'select',
      label: 'Rahmen',
      options: [
        { label: 'Kein', value: 'none' },
        { label: 'Dünner Rahmen', value: 'thin' }
      ]
    },
    borderColor: {
      type: 'select',
      label: 'Rahmenfarbe',
      options: [
        { label: 'Grau 200', value: 'gray-200' },
        { label: 'Grau 300', value: 'gray-300' },
        { label: 'Blau 200', value: 'blue-200' },
        { label: 'Slate 700', value: 'slate-700' }
      ],
      condition: (values) => values.border !== 'none'
    },
    borderRadius: {
      type: 'select',
      label: 'Abrundung',
      options: [
        { label: 'Keine', value: 'none' },
        { label: 'Klein', value: 'sm' },
        { label: 'Groß', value: 'lg' },
        { label: 'Sehr groß', value: 'xl' }
      ]
    }
  },
  defaultProps: {
    gap: 'lg',
    align: 'stretch',
    backgroundColor: 'transparent',
    padding: 'xl',
    maxWidth: '6xl',
    border: 'none',
    borderColor: 'gray-200',
    borderRadius: 'lg'
  },
  render: StackComponent
};
